import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Field } from 'formik';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _omit from 'lodash/omit';

import * as questionValue from '../../constants/questionValue';
import VisuallyHidden from '../../components/VisuallyHidden/VisuallyHidden';
import FieldRadio from '../../components/FieldRadio/FieldRadio';

import './fieldset-most-least.scss';

class FieldSetMostLeast extends React.Component {
    constructor(props) {
        super(props);

        this.handleChange = this.handleChange.bind(this);
    }

    handleChange(event) {
        const { field, form, options } = this.props;
        const { name, value } = event.target;

        options.forEach((option) => {
            const optionName = `${field.name}.option-${option.id}`;
            if (
                optionName !== name &&
                _get(form.values, optionName) === value
            ) {
                const values = Object.assign({}, form.values);
                values[field.name] = _omit(values[field.name], [`option-${option.id}`]);
                _set(values, name, value);
                form.setValues(values);
            }
        });
    }

    render() {
        const {
            form,
            id,
            field,
            label,
            options,
            assessmentLanguage,
        } = this.props;
        const fieldName = field.name;
        const fieldId = id || fieldName;
        const error = form.errors[fieldName];
        const isInvalid = error !== undefined && error !== null;
        const isTouched = !!form.touched[fieldName];
        const className = classNames('fieldset-most-least', {
            'fieldset-most-least--invalid': isTouched && isInvalid,
        });
        let most = 'Most';
        let least = 'Least';
        if (assessmentLanguage) {
            if (assessmentLanguage === 'SPA' || assessmentLanguage === 'LAS') {
                most = 'Más';
                least = 'Menos';
            } else if (assessmentLanguage === 'FRE') {
                most = 'Très';
                least = 'Moins';
            } else if (assessmentLanguage === 'POR' || assessmentLanguage === 'POE') {
                most = 'Maioria';
                least = 'Mínimo';
            } else if (assessmentLanguage === 'CHI') {
                most = '大部分';
                least = '至少';
            } else if (assessmentLanguage === 'CZE') {
                most = 'Většina';
                least = '- Nejméně';
            } else if (assessmentLanguage === 'RUS') {
                most = 'Большинство';
                least = 'По крайней мере';
            } else if (assessmentLanguage === 'GER') {
                most = 'Die meisten';
                least = 'Geringst';
            } else if (assessmentLanguage === 'DUT') {
                most = 'De meeste';
                least = 'Minst';
            } else if (assessmentLanguage === 'IND') {
                most = 'Kebanyakan';
                least = 'Setidaknya';
            } else if (assessmentLanguage === 'DAN') {
                most = 'De fleste';
                least = 'Mindst';
            } else if (assessmentLanguage === 'FIN') {
                most = 'Eniten';
                least = 'Vähiten';
            } else if (assessmentLanguage === 'JPN') {
                most = 'ほとんど';
                least = '最低';
            } else if (assessmentLanguage === 'KOR') {
                most = '대부분';
                least = '최소';
            }
        }
        return (
            <div
                aria-labelledby={`fieldset-${fieldId}-label`}
                className={className}
                id={`fieldset-${fieldId}`}
                role="group"
            >
                <span
                    className="fieldset-most-least__question"
                    id={`fieldset-${fieldId}-label`}
                >
                    {label}
                </span>
                <div className="fieldset-most-least__options">
                    <div className="fieldset-most-least__row">
                        <span
                            className="fieldset-most-least__label"
                            id={`most-${fieldId}-label`}
                        >
                            {most}
                            <VisuallyHidden>:</VisuallyHidden>
                        </span>
                        <span
                            className="fieldset-most-least__label"
                            id={`least-${fieldId}-label`}
                        >
                            {least}
                            <VisuallyHidden>:</VisuallyHidden>
                        </span>
                    </div>
                    {
                        options.map((option) => {
                            const optionName = `option-${option.id}`;
                            return (
                                <div
                                    className="fieldset-most-least__row"
                                    key={option.id}
                                >
                                    <div className="fieldset-most-least__option">
                                        <Field
                                            appearance="default"
                                            component={FieldRadio}
                                            id={`${fieldId}-${option.id}-most`}
                                            name={`${fieldName}.${optionName}`}
                                            onChange={this.handleChange}
                                            value={questionValue.MOST}
                                            ariaLabelledby={`most-${fieldId}-label radio-${fieldId}-${option.id}-label`}
                                        />
                                    </div>
                                    <div className="fieldset-most-least__option">
                                        <Field
                                            appearance="default"
                                            component={FieldRadio}
                                            id={`${fieldId}-${option.id}-least`}
                                            name={`${fieldName}.${optionName}`}
                                            onChange={this.handleChange}
                                            value={questionValue.LEAST}
                                            ariaLabelledby={`least-${fieldId}-label radio-${fieldId}-${option.id}-label`}
                                        />
                                    </div>
                                    <div
                                        className="fieldset-most-least__statement"
                                        id={`radio-${fieldId}-${option.id}-label`}
                                    >
                                        {option.text}
                                    </div>
                                </div>
                            );
                        })
                    }
                </div>
            </div>
        );
    }
}

FieldSetMostLeast.propTypes = {
    field: PropTypes.shape({
        name: PropTypes.string,
        onBlur: PropTypes.func,
        onChange: PropTypes.func,
        value: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({}),
        ]),
    }).isRequired,
    form: PropTypes.shape({
        errors: PropTypes.objectOf(PropTypes.oneOfType([
            PropTypes.shape({}),
            PropTypes.string,
        ])),
        touched: PropTypes.objectOf(PropTypes.oneOfType([
            PropTypes.bool,
            PropTypes.shape({}),
        ])),
    }).isRequired,
    label: PropTypes.string.isRequired,
    id: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    options: PropTypes.arrayOf(PropTypes.shape({})),
    assessmentLanguage: PropTypes.string.isRequired,
};

FieldSetMostLeast.defaultProps = {
    id: '',
    options: [],
};

export default FieldSetMostLeast;
